import { useCallback, useContext, useEffect, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { ToggleButtonPill, ToggleButtonPillGroup } from '@hungryroot/ramen';
import {
  CustomerFoodFeedbackPreference,
  type FoodFeedback,
} from '@hungryroot/rest';
import { foodFeedbackIdMap } from '../../../hooks/useFoodFeedback';
import { FoodFeedbackModalContext } from '../FoodFeedbackModal';
import { type useDetectChanges } from '../useDetectChanges';
import { FoodFeedbackSecondary } from './FoodFeedbackSecondary';
import {
  useFoodFeedbackItems,
  type UseFoodFeedbackItemsProps,
} from './useFoodFeedbackItems';
import { useFoodFeedbackUpdates } from './useFoodFeedbackUpdates';
import styles from './FoodFeedbacks.module.css';

type FoodFeedbacksProps = {
  className?: string;
  enableApiCalls: boolean;
  productRatingId?: number | null;
  enableInitialFetch?: boolean;
  changeDetection: ReturnType<typeof useDetectChanges>;
} & UseFoodFeedbackItemsProps;

export function FoodFeedbacks(props: FoodFeedbacksProps) {
  const {
    rating,
    enableApiCalls,
    item,
    productRatingId,
    enableInitialFetch,
    changeDetection,
  } = props;

  const foodFeedbackItems = useFoodFeedbackItems(props);
  const [latestFoodFeedbackSelection, setLatestFoodFeedbackSelection] =
    useState<number | null>(null);

  const [
    customerNutritionalFoodFeedbackId,
    setCustomerNutritionalFoodFeedbackId,
  ] = useState<number | null>(null);

  const handleIntersection = useCallback(
    (inView: boolean, entry: IntersectionObserverEntry) => {
      if (!inView && entry?.target) {
        entry.target.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    },
    []
  );

  if (rating === null) {
    return null;
  }

  return (
    <div
      className={styles['food-feedbacks-container']}
      data-testid="food-feedbacks-container"
    >
      <ToggleButtonPillGroup>
        {foodFeedbackItems.map((foodFeedbackItem) => {
          let keyId;

          if ('bundleId' in item && item.bundleId) {
            keyId = item.bundleId;
          } else if ('pairingId' in item && item.pairingId) {
            keyId = item.pairingId;
          } else if ('productId' in item && item.productId) {
            keyId = item.productId;
          }

          return (
            <FoodFeedbackItemToggle
              key={`${rating}-${item.type}-${keyId}-${foodFeedbackItem.id}`}
              foodFeedbackItem={foodFeedbackItem}
              item={item}
              onChange={(isSelected) => {
                if (isSelected) {
                  setLatestFoodFeedbackSelection(foodFeedbackItem.id);
                } else {
                  setLatestFoodFeedbackSelection(null);
                }

                changeDetection.updateFoodFeedback({
                  foodFeedbackId: foodFeedbackItem.id,
                  prevValue: !isSelected,
                  newValue: isSelected,
                });
              }}
              rating={rating}
              enableApiCalls={enableApiCalls}
              enableInitialFetch={enableInitialFetch}
              onSuccessfulChange={({
                customerFoodFeedbackId,
                foodFeedbackId,
              }) => {
                if (foodFeedbackId === foodFeedbackIdMap.Nutrition) {
                  setCustomerNutritionalFoodFeedbackId(customerFoodFeedbackId);
                }
              }}
            />
          );
        })}
      </ToggleButtonPillGroup>
      <InView as="div" triggerOnce onChange={handleIntersection} />
      <FoodFeedbackSecondary
        enableApiCalls={enableApiCalls}
        productRatingId={productRatingId}
        customerNutritionalFoodFeedbackId={customerNutritionalFoodFeedbackId}
        foodFeedbackSelection={latestFoodFeedbackSelection}
        rating={rating}
        item={item}
        changeDetection={changeDetection}
      />
    </div>
  );
}

type FoodFeedbackItemToggleProps = {
  foodFeedbackItem: FoodFeedback;
  onChange: (isSelected: boolean) => void;
  enableApiCalls: boolean;
  onSuccessfulChange?: ({
    customerFoodFeedbackId,
    foodFeedbackId,
  }: {
    customerFoodFeedbackId: number | null;
    foodFeedbackId: number;
  }) => void;
  enableInitialFetch?: boolean;
} & Pick<UseFoodFeedbackItemsProps, 'item' | 'rating'>;

function FoodFeedbackItemToggle(props: FoodFeedbackItemToggleProps) {
  const {
    foodFeedbackItem,
    item,
    onChange,
    onSuccessfulChange,
    rating,
    enableApiCalls,
    enableInitialFetch,
  } = props;

  const { issues } = useContext(FoodFeedbackModalContext);

  // If this component is unmounted, remove any issues from the
  // set.
  useEffect(() => {
    return () => {
      issues?.delete(item.orderNumber);
    };
  }, [item.orderNumber, issues]);

  const { toggled, onToggle } = useFoodFeedbackUpdates({
    onSuccessfulChange,
    item,
    enabled: enableApiCalls,
    foodFeedbackId: foodFeedbackItem.id,
    enableInitialFetch,
    foodFeedbackPreference: rating
      ? CustomerFoodFeedbackPreference.Like
      : CustomerFoodFeedbackPreference.Dislike,
  });

  return (
    <ToggleButtonPill
      label={foodFeedbackItem.name}
      isSelected={toggled}
      onChange={(isSelected) => {
        if (foodFeedbackItem.id === foodFeedbackIdMap.ItemIssue) {
          isSelected
            ? issues?.add(item.orderNumber)
            : issues?.delete(item.orderNumber);
        }

        onChange(isSelected);
        onToggle();
      }}
    />
  );
}
